import '@esotericsoftware/spine-player/dist/spine-player.min.css';

import { SpinePlayer, SpinePlayerConfig } from '@esotericsoftware/spine-player';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ISpineResult } from '@/material/form-upload/folder';
import { uploadCosImage } from '@/material/form-upload/service';
import { SpinePlayerRef } from '@/material/form-upload/spine';
import { base64ToFile } from '@/utils/file';

import styles from './index.module.less';

const SpineWebPlayer: React.FC<{
  value?: ISpineResult;
  disabled?: boolean;
  playerRef?: React.ForwardedRef<SpinePlayerRef>
  showControls?: boolean,
  config?: {
    width: string;
    height: string;
  }
  playerConfig?: Partial<SpinePlayerConfig>;
  onChange?: (val: ISpineResult) => void;
}> = ({ value = {}, playerRef, config, playerConfig, showControls = true, onChange }) => {
  const spineRef = useRef<HTMLDivElement | null>(null);

  const [app, setApp] = useState<SpinePlayer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentAnimation, setCurrentAnimation] = useState<string>('');

  useImperativeHandle(playerRef, () => ({
    getAnimation: () => getCurrentAnimation(app),
    getImage: () => getImage(app),
  }));

  useEffect(() => {
    let mounted = true;
    let currentApp: SpinePlayer | null = null;

    const initPlayer = async () => {
      if (!spineRef.current || !value?.skel) return;

      if (app) {
        app.dispose();
        setApp(null);
      }

      try {
        const initConfig = value?.json ? {
          atlasUrl: value?.atlas,
          jsonUrl: value?.json,
        } : {
          skeleton: value?.skel,
          atlas: value?.atlas,
        };
        currentApp = new SpinePlayer(spineRef.current, {
          ...initConfig,
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
          animation: value?.animation,
          showControls,
          alpha: true,
          viewport: {
            padLeft: '0%',
            padRight: '0%',
            padTop: '0%',
            padBottom: '0%',
          },
          success: async (_player: SpinePlayer) => {
            if (value.thumbnail) return;
            await new Promise(resolve => setTimeout(resolve, 1000));
            const thumbnail = await getImage(currentApp);
            if (thumbnail) onChange?.({ ...value, thumbnail });
          },
          error: (_player: SpinePlayer, msg: string) => {
            if (mounted) {
              setError(msg);
            }
          },
          ...playerConfig,
        });

        if (mounted) {
          setApp(currentApp);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize SpinePlayer');
        }
      }
    };

    initPlayer();

    // 清理函数
    return () => {
      mounted = false;
      if (currentApp) {
        currentApp.dispose();
      }
    };
  }, [value?.atlas, value?.skel]);

  const getValue = () => {
    if (app?.skeleton?.data.animations) {
      const { animations } = app.skeleton.data;
      console.log('All available animations:', animations);
      return animations;
    }
    return null;
  };

  const getFirstAnimation = () => {
    if (app?.skeleton?.data.animations) {
      const { animations } = app.skeleton.data;
      console.log('All available animations:', animations);
      return animations[0]?.name;
    }
    return '';
  };

  const getCurrentAnimation = (app: SpinePlayer | null) => {
    if (app?.animationState) {
      const currentTrack = app?.animationState?.tracks[0];
      if (currentTrack) {
        const animationName = currentTrack?.animation?.name;
        setCurrentAnimation(animationName || '');
        if (animationName?.includes('<empty>')) {
          return '';
        }
        return animationName;
      }
    }
    return getFirstAnimation();
  };

  const getImage = async (app: SpinePlayer | null) => {
    if (app?.canvas) {
      const imgData = app?.canvas.toDataURL('image/webp', 0.8);
      const file = base64ToFile(imgData, `${value?.directoryName}_thumbnail.webp`);
      const res = await uploadCosImage(file);
      console.log('Get spine thumbnail successfully: ', res);
      return res?.response?.url;
    }
    return '';
  };

  if (error) {
    return (
      <div className={styles.errorTips} style={{ width: config?.width || 370, height: config?.height || 280 }}>
        播放器加载失败，请检查文件是否正确
        或请重新打开预览或升级为最新版本chrome浏览器、QQ浏览器
      </div>
    );
  }

  return (
    <div>
      <div
        id="spine-player-container"
        ref={spineRef}
        style={{
          width: config?.width || 370,
          height: config?.height || 280,
          backgroundColor: '#ccc',
          position: 'relative',
        }}
      />
      <div style={{ marginTop: '20px', display: 'none' }}>
        <div>Current Animation: {currentAnimation}</div>
        <div
          onClick={getValue}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            cursor: 'pointer',
            borderRadius: '4px',
            marginBottom: '10px',
          }}
        >
          get All Animations
        </div>
        <div
          onClick={() => getCurrentAnimation(app)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            borderRadius: '4px',
          }}
        >
          get Current Animation
        </div>
      </div>
    </div>
  );
};

export default SpineWebPlayer;
